import { UIViewSpec } from './view';

export interface UITouch {
    readonly id: number;
    ox: number;
    oy: number;
    x: number;
    y: number;
    dx: number;
    dy: number;
    vx: number;
    vy: number;
    time: number;
    kind: 'mouse' | 'touch';
}

export interface UITouchEvent {
    elem: HTMLElement;
    target: Element;
    touch: UITouch;
}

export interface UITouchDelegate {
    touchMove?: (event: UITouchEvent) => void | boolean;
    touchEnd?: (event: UITouchEvent) => void | boolean;
    stop?: boolean;
}

export interface UITouchOptions {
    mouse?: boolean;
    touch?: boolean;
}

export type UITouchHandler = (event: UITouchEvent) => false | UITouchDelegate;

export interface UIHoverEvent {
    elem: HTMLElement;
    hover: boolean;
}

export type UIHoverHandler = (event: UIHoverEvent) => void;

declare global {
    interface Element {
        ui_mouse_handlers?: UITouchHandler[];
        ui_touch_handlers?: UITouchHandler[];
        ui_hover_handlers?: UIHoverHandler[];
        ui_interact_handlers?: UITouchHandler[];
    }
}

export function uiTouchIn(touch: UITouch, elem: HTMLElement) {
    const rect = elem.getBoundingClientRect();
    return [touch.x - rect.left, touch.y - rect.top];
}

interface UIStoredTouch extends UITouch {
    delegates: UITouchDelegate[];
}

function updateTouch(touch: UITouch, x: number, y: number, time: number) {
    const dx = x - touch.x;
    const dy = y - touch.y;
    let dt = time - touch.time;

    if(dt < 1) dt = 1;

    const w = (dt < 200) ? dt / 200 : 1;

    touch.x = x;
    touch.y = y;
    touch.dx = x - touch.ox;
    touch.dy = y - touch.oy;
    touch.vx = w * (dx * 1000 / dt) + (1 - w) * touch.vx;
    touch.vy = w * (dy * 1000 / dt) + (1 - w) * touch.vy;
    touch.time = time;

    return dx || dy;
}

function initMouse(elem: HTMLElement) {
    const handlers = elem.ui_mouse_handlers = [] as UITouchHandler[];

    elem.addEventListener('mousedown', e => {
        if(e.button !== 0) return;

        const touch: UITouch = { id: -1, ox: e.clientX, oy: e.clientY, x: e.clientX, y: e.clientY, dx: 0, dy: 0, vx: 0, vy: 0, time: e.timeStamp, kind: 'mouse' };

        const event = { elem: elem, touch: touch, target: e.target as Element };

        const delegates: UITouchDelegate[] = [];
        let need_move = false;
        let stop = false;

        for(const h of handlers) {
            const d = h(event);
            if(d) {
                if(d.stop)
                    stop = true;
                delegates.push(d);
                if(d.touchMove)
                    need_move = true;
            }
        }

        if(stop) {
            e.stopPropagation();
            e.preventDefault();
        }

        if(delegates.length) {
            let move_listener: ((e: MouseEvent) => void) | undefined;

            if(need_move) {
                move_listener = (e: MouseEvent) => {
                    e.preventDefault();

                    if(updateTouch(touch, e.clientX, e.clientY, e.timeStamp)) {
                        const event = { elem: elem, touch: touch, target: e.target as Element };

                        for(const d of delegates) {
                            d.touchMove?.(event);
                        }
                    }
                }

                document.addEventListener('mousemove', move_listener);
            }

            const end_listener = (e: MouseEvent) => {
                e.preventDefault();
                updateTouch(touch, e.clientX, e.clientY, e.timeStamp);
                const event = { elem: elem, touch: touch, target: e.target as Element };

                for(const d of delegates) {
                    d.touchEnd?.(event);
                }

                document.removeEventListener('mouseup', end_listener);
                move_listener && document.removeEventListener('mousemove', move_listener);
            }

            document.addEventListener('mouseup', end_listener);
        }
    });
}

function initTouch(elem: HTMLElement) {
    const handlers = elem.ui_touch_handlers = [] as UITouchHandler[];

    let touch_handlers_init = false;
    const touches: { [index: number]: UIStoredTouch } = {};

    function touchStarted(touch: UIStoredTouch, target: Element) {
        const event = { elem: elem, touch: touch, target: target };
        const delegates = touch.delegates;

        let stop = false;

        for(const h of handlers) {
            const d = h(event);
            if(d) {
                if(d.stop)
                    stop = true;
                delegates.push(d);
            }
        }

        touches[touch.id] = touch;

        return stop;
    }

    function touchChanged(touch: UIStoredTouch, target: Element) {
        const event = { elem: elem, touch: touch, target: target };
        const delegates = touch.delegates;

        let prevent = false;

        for(const d of delegates) {
            if(d.touchMove && d.touchMove(event))
                prevent = true;
        }

        return prevent;
    }

    function touchEnded(touch: UIStoredTouch, target: Element) {
        const event = { elem: elem, touch: touch, target: target };
        const delegates = touch.delegates;

        let prevent = false;

        for(const d of delegates) {
            if(d.touchEnd && d.touchEnd(event))
                prevent = true;
        }

        delete touches[touch.id];

        return prevent;
    }

    let last_prevent = false;

    const touch_handler = (e: TouchEvent) => {
        const ts = e.changedTouches;
        const time = e.timeStamp;
        let prevent = false;
        let stop = false;
        let target = e.target as Element;

        for(let i = 0; i < ts.length; i++) {
            let t = ts[i];
            const id = t.identifier;
            const x = t.clientX;
            const y = t.clientY;

            const touch = touches[id];

            if(x < -100) {
                if(touch)
                    touchEnded(touch, target);
                continue;
            }

            if(e.type === 'touchstart') {
                if(touch) continue;

                stop = touchStarted({ id: id, ox: x, oy: y, x: x, y: y, dx: 0, dy: 0, vx: 0, vy: 0, time: time, kind: 'touch', delegates: [] }, target) || stop;

                if(!touch_handlers_init) {
                    touch_handlers_init = true;
                    elem.addEventListener('touchmove', touch_handler);
                    elem.addEventListener('touchend', touch_handler);
                    elem.addEventListener('touchcancel', touch_handler);
                }
            } else {
                if(!touch) continue;

                if(updateTouch(touch, x, y, time)) {
                    if(touchChanged(touch, target))
                        prevent = true;
                } else {
                    prevent = last_prevent;
                }

                if(e.type === 'touchend' || e.type === 'touchcancel') {
                    if(touchEnded(touch, target))
                        prevent = true;
                }
            }
        }

        prevent = prevent || stop;
        last_prevent = prevent;
        prevent && e.preventDefault();
        stop && e.stopPropagation();
    };

    elem.addEventListener('touchstart', touch_handler);
}

export function onTouch(where: UIViewSpec, handler: UITouchHandler, options: UITouchOptions = {}) {
    const elem = where.ui_elem;

    if(options.mouse !== false) {
        if(!elem.ui_mouse_handlers)
            initMouse(elem);

        elem.ui_mouse_handlers!.push(handler);
    }

    if(options.touch !== false) {
        if(!elem.ui_touch_handlers)
            initTouch(elem);

        elem.ui_touch_handlers!.push(handler);
    }
}

let hover_init = false;

function processHover(to: Element | null, from: Element | null, over: boolean) {
    for(; to; to = to.parentElement) {
        if(to.ui_hover_handlers && (!from || !to.contains(from))) {
            for(const h of to.ui_hover_handlers!) {
                h({ elem: to as HTMLElement, hover: over })
            }
        }
    }
}

export function onHover(where: UIViewSpec, handler: UIHoverHandler) {
    if(!hover_init) {
        const body = document.body;

        body.addEventListener('mouseover', ev => {
            const target = ev.target as Element;
            const related = ev.relatedTarget as Element;

            if(related)
                processHover(related, target, false);
            processHover(target, related, true);
        });

        body.addEventListener('mouseout', ev => {
            const target = ev.target as Element;
            const related = ev.relatedTarget as Element;

            if(!related)
                processHover(target, null, false);
        });

        hover_init = true;
    }

    const elem = where.ui_elem;

    if(elem.ui_hover_handlers)
        elem.ui_hover_handlers.push(handler);
    else
        elem.ui_hover_handlers = [handler];
}

let interact_init = false;

export function onInteract(where: UIViewSpec, handler: UITouchHandler) {
    if(!interact_init) {
        interact_init = true;

        onTouch(document.body, e => {
            for(let target: Element | null | undefined = e.target.closest('.x-interact'); target; target = target.parentElement?.closest('.x-interact')) {
                if(target.ui_interact_handlers) {
                    for(const h of target.ui_interact_handlers!) {
                        const result = h(e);
                        if(result) return result;
                    }
                }
            }

            return false;
        });
    }

    where = where.ui_elem;

    if(where.ui_interact_handlers) {
        where.ui_interact_handlers.push(handler);
    } else {
        where.classList.add('x-interact');
        where.ui_interact_handlers = [handler];
    }
}
